import { useIntl } from 'react-intl';
import SummaryItem from 'client/components/ui/SummaryItem/SummaryItem';
import EnquiryWarning from 'client/pages/EnquiriesPage/components/EnquiryWarning/EnquiryWarning';
import EnquiryDetails from 'client/pages/EnquiriesPage/components/EnquiryDetails/EnquiryDetails';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import getTranslationOrEnglish from 'client/utils/i18n/i18nUtils';
import moment from 'moment';

const EnquirySummary = ({ enquiry, activityTypes, showSummary, change, changeEnquiryType, canChangeEnquiryType = true, assetOwners }) => {
    const intl = useIntl();
    const activityType = activityTypes.find(activityType => activityType.systemId === enquiry.activityType);
    const currentLocale = intl.locale;
    const activityTypeValue = activityType ? getTranslationOrEnglish(activityType.value, currentLocale) : null;
    const activityTypeCategory = activityType ? getTranslationOrEnglish(activityType.category, currentLocale) : null;
    const isEmergency = enquiry?.isEmergency ?? enquiry?.emergency ?? false;
    const enquiryTypeMessageId = isEmergency ? 'EnquirySummary.enquiryType.emergency' : 'EnquirySummary.enquiryType.planned';

    const formatDate = (date) => {
        if (!date) {
            return 'N/A';
        }

        const formatted = moment(date);
        return formatted.isValid() ? formatted.format('D MMMM YYYY') : 'N/A';
    };

    return (
        <>
            {/* Enquiry Type and Title Section */}
            <div className="govuk-!-margin-top-4">
                <EnquiryDetails className="govuk-!-margin-top-4">
                    {intl.formatMessage({ id: 'EnquirySummary.type.title' })}
                </EnquiryDetails>
            </div>
            <div className="govuk-!-margin-top-4">
                <SummaryItem
                    title={intl.formatMessage({ id: 'EnquirySummary.enquiryType' })}
                    showChangeOption={showSummary}
                    change={changeEnquiryType || change}
                    disabled={!canChangeEnquiryType}
                    data-testid={'enquiry-summary-item-title'}
                >
                    {intl.formatMessage({ id: enquiryTypeMessageId })}
                </SummaryItem>
                <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible"/>
            </div>

            {/* Enquiry Details and Title Section */}
            <div className="govuk-!-margin-top-8">
                <EnquiryDetails>
                    {intl.formatMessage({ id: 'EnquirySummary.details.title' })}
                </EnquiryDetails>
                <SummaryItem title={intl.formatMessage({ id: 'EnquirySummary.title' })} showChangeOption={showSummary} change={change} data-testid={'enquiry-summary-item-title'}>
                    {enquiry?.title ? enquiry?.title : 'N/A' }
                </SummaryItem>
            </div>

            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible"/>

            <SummaryItem title={intl.formatMessage({ id: 'EnquirySummary.jobReference' })} showChangeOption={showSummary} change={change}>
                {enquiry?.jobReference ? enquiry?.jobReference : '' }
            </SummaryItem>
            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible "/>

            <SummaryItem title={intl.formatMessage({ id: 'EnquirySummary.activity.type.category' })} showChangeOption={showSummary} change={change}>
                <div>
                    {activityTypeCategory}
                </div>
            </SummaryItem>

            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible "/>

            <SummaryItem title={intl.formatMessage({ id: 'EnquirySummary.activity.type.value' })} showChangeOption={showSummary} change={change}>
                <div>
                    {activityTypeValue}
                </div>
            </SummaryItem>

            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible "/>
            <SummaryItem title={intl.formatMessage({ id: 'EnquirySummary.description' })} showChangeOption={showSummary} change={change}>
                {enquiry?.description ? enquiry?.description : 'N/A' }
            </SummaryItem>
            {!isEmergency && (
                <>
                    <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible "/>
                    <SummaryItem title={intl.formatMessage({ id: 'EnquirySummary.startDate' })} showChangeOption={showSummary} change={change}>
                        {formatDate(enquiry?.startDate)}
                    </SummaryItem>

                    <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible "/>

                    <SummaryItem title={intl.formatMessage({ id: 'EnquirySummary.endDate' })} showChangeOption={showSummary} change={change}>
                        {formatDate(enquiry?.endDate)}
                    </SummaryItem>
                </>
            )}

            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible "/>
            <EnquiryWarning
                showViewableWarning={false}
                showSafetyLink={false}
            />
        </>
    );
};

EnquirySummary.propTypes = {
    ...defaultAriaProps
};

EnquirySummary.defaultProps = {
    showSummary: false
};

export default EnquirySummary;
